import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";

const card = { background: "#111", border: "1px solid #282828", borderRadius: 11, padding: "11px 12px" };
const fmtBytes = value => {
  const n = Number(value || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};
const fmtMetric = metric => {
  if (!metric) return "—";
  if (metric.unit === "bytes") return `${fmtBytes(metric.used)} / ${metric.limit ? fmtBytes(metric.limit) : "—"}`;
  return `${Number(metric.used || 0).toLocaleString()} / ${metric.limit == null ? "—" : Number(metric.limit).toLocaleString()}`;
};
const pct = value => Number.isFinite(Number(value)) ? `${Number(value).toFixed(1)}%` : "—";
const riskText = value => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "Sin límite reportado";
  if (n >= 85) return "Crítico";
  if (n >= 70) return "Preparar upgrade";
  if (n >= 50) return "Vigilar";
  return "OK";
};

export default function AdminResourceUsagePanel() {
  const [latest, setLatest] = useState({ supabase: null, cloudinary: null });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadHistory = useCallback(async () => {
    const { data } = await supabase.from("resource_usage_snapshots")
      .select("id,provider,plan,usage,captured_at")
      .order("captured_at", { ascending: false })
      .limit(20);
    const rows = data || [];
    setHistory(rows);
    const sb = rows.find(row => row.provider === "supabase");
    const cl = rows.find(row => row.provider === "cloudinary");
    setLatest({ supabase: sb ? sb.usage : null, cloudinary: cl ? (cl.usage?.summary || cl.usage) : null });
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const refresh = async () => {
    setLoading(true); setError("");
    try {
      const { data, error: fnError } = await supabase.functions.invoke("admin-resource-usage", { body: {} });
      if (fnError) throw fnError;
      if (!data?.ok) throw new Error(data?.error || "No se pudo consultar el consumo");
      setLatest({ supabase: data.supabase, cloudinary: data.cloudinary });
      await loadHistory();
    } catch (err) { setError(err?.message || String(err)); }
    finally { setLoading(false); }
  };

  const sb = latest.supabase;
  const cl = latest.cloudinary;
  const cloudCredit = cl?.credits;
  const latestAt = useMemo(() => history[0]?.captured_at ? new Date(history[0].captured_at).toLocaleString("es-MX") : null, [history]);

  const MetricCard = ({ label, metric }) => <div style={card}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
      <span style={{ color: "#aaa", fontSize: 10, fontWeight: 800 }}>{label}</span>
      <span style={{ color: Number(metric?.percent) >= 70 ? "#f6c453" : "#81c784", fontSize: 11, fontWeight: 900 }}>{pct(metric?.percent)}</span>
    </div>
    <p style={{ color: "#ddd", fontSize: 11, margin: "6px 0 2px" }}>{fmtMetric(metric)}</p>
    <p style={{ color: "#555", fontSize: 9, margin: 0 }}>{riskText(metric?.percent)}</p>
  </div>;

  return <section aria-label="Consumo de infraestructura" style={{ marginTop: 22 }}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-end", marginBottom: 10 }}>
      <div>
        <p style={{ color: "#555", fontWeight: 700, fontSize: 11, letterSpacing: 1, margin: "0 0 4px" }}>INFRAESTRUCTURA · CONSUMO</p>
        <p style={{ color: "#777", fontSize: 10, margin: 0 }}>Cada actualización guarda una fotografía histórica del uso.</p>
      </div>
      <button onClick={refresh} disabled={loading} style={{ background: "#1a1a2e", border: "1px solid #33335a", color: "#90caf9", borderRadius: 9, padding: "8px 10px", fontSize: 10, fontWeight: 800, cursor: loading ? "wait" : "pointer" }}>{loading ? "Consultando…" : "↻ Actualizar"}</button>
    </div>
    {error && <p style={{ background: "#351515", border: "1px solid #5a2525", color: "#ff8a80", borderRadius: 9, padding: 9, fontSize: 10 }}>{error}</p>}

    <div style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 12, padding: 12, marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 9 }}><strong style={{ color: "#fff", fontSize: 12 }}>Supabase · {sb?.plan || "Free"}</strong><span style={{ color: "#555", fontSize: 9 }}>{latestAt || "Sin captura"}</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 8 }}>
        <MetricCard label="BASE DE DATOS" metric={sb?.database} />
        <MetricCard label="STORAGE" metric={sb?.storage} />
      </div>
      {sb && <p style={{ color: "#555", fontSize: 9, lineHeight: 1.45, margin: "8px 0 0" }}>Límites Free registrados: 50,000 MAU · 5 GB egress · 500,000 Edge Functions · 2 M mensajes Realtime. El uso corriente de esos cuatro rubros se consulta en el dashboard del proveedor; DB y Storage sí se miden directamente aquí.</p>}
    </div>

    <div style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 12, padding: 12, marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 9 }}><strong style={{ color: "#fff", fontSize: 12 }}>Cloudinary · {cl?.plan || "—"}</strong><span style={{ color: "#555", fontSize: 9 }}>{latestAt || "Sin captura"}</span></div>
      {cl?.error ? <p style={{ color: "#e57373", fontSize: 10, margin: 0 }}>{cl.error}</p> : <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 8 }}>
        <MetricCard label="CRÉDITOS" metric={cloudCredit} />
        <MetricCard label="STORAGE" metric={cl?.storage} />
        <MetricCard label="BANDWIDTH" metric={cl?.bandwidth} />
        <MetricCard label="TRANSFORMACIONES" metric={cl?.transformations} />
      </div>}
    </div>

    {history.length > 0 && <details style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 10, padding: "9px 11px" }}>
      <summary style={{ color: "#777", fontSize: 10, fontWeight: 800, cursor: "pointer" }}>HISTORIAL DE CAPTURAS ({history.length})</summary>
      <div style={{ marginTop: 8, display: "grid", gap: 5 }}>{history.slice(0, 12).map(row => <div key={row.id} style={{ color: "#666", fontSize: 9, display: "flex", justifyContent: "space-between", gap: 8 }}><span>{row.provider.toUpperCase()} · {row.plan || "—"}</span><span>{new Date(row.captured_at).toLocaleString("es-MX")}</span></div>)}</div>
    </details>}
  </section>;
}
